import React, { Fragment, useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';

import { generatePath } from '@klp/routing';

import { FileObject, MimeTypes } from '../classes';
import type { IFileObject } from '../classes';
import { FilePreview } from '../components';
import { Gallery } from '../components/Gallery';
import { FileRoutes } from '../config';
import { useFileContext } from '../context/FileContext';

interface IProps {
    source: Array<FileObject>;
    allowedMimeTypes: Array<string>;
    path: string;
    queueName: string;
    edit?: boolean;
    onChange?: any;
    thumbnail?: boolean;
    uploader?: any;
    uploaderParams?: any;
    fileContentField?: string;
    'data-testid'?: string;
}

export const FileGallery: React.FC<IProps> = (props) => {
    const {
        source,
        path,
        queueName,
        uploader,
        uploaderParams,
        allowedMimeTypes,
        edit,
        fileContentField,
        onChange,
    } = props;

    const { downloadQueue, uploadQueue, removeFromQueue } = useFileContext();

    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    useEffect(() => {
        if (onChange) {
            onChange(queueName, downloadQueue, uploadQueue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queueName, downloadQueue, uploadQueue]);

    const handleRemove = (file: FileObject) => {
        removeFromQueue(file);
    };

    const handleClick = (imageIndex: number) => (file: FileObject) => {
        if (!MimeTypes.image.includes(file?.type || '')) {
            return;
        }
        setSelectedImageIndex(imageIndex);
    };

    const filteredSource = [...source].filter((file: FileObject) =>
        allowedMimeTypes.includes(file?.type || ''),
    );

    const filesList =
        uploaderParams?.queueName && uploadQueue?.[uploaderParams.queueName]
            ? [...filteredSource, ...uploadQueue[uploaderParams.queueName]]
            : filteredSource;

    const imageParams = {
        maxwidth: 200,
        maxheight: 200,
        trimToFit: true,
    };

    const FileUploadComponent = uploader;

    return (
        <>
            <Grid
                container={true}
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                className="Gallery FileUploaderPreview__Grid"
                data-testid={props['data-testid']}
            >
                {filesList.map((file: IFileObject, index: number) => {
                    const filePath = generatePath(path, file.id);
                    return (
                        <Fragment key={file.id || index}>
                            <FilePreview
                                fileObject={
                                    new FileObject({
                                        ...file,
                                        imageParams,
                                        filePath,
                                        queueName,
                                    })
                                }
                                fileContentField={fileContentField}
                                edit={edit}
                                onRemove={uploader ? handleRemove : undefined}
                                onClick={handleClick(index + 1)}
                            />
                        </Fragment>
                    );
                })}
                {uploader && <FileUploadComponent {...uploaderParams} />}
            </Grid>
            <Gallery
                images={filesList.map((file: FileObject) =>
                    FileRoutes.preview(path, file.id),
                )}
                path={path}
                selectedImageIndex={selectedImageIndex}
            />
        </>
    );
};
